// Run detail — live status, budget gauges, audit event log. The
// page polls every 3s while the run is active so the operator sees
// state transitions, budget pressure, and supervisor events in near
// real-time without a websocket.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { useQuery } from "@tanstack/react-query";
import { Card, SectionCard } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { FundingPanel } from "@/components/FundingPanel";
import { LifecycleActions } from "@/components/runs/LifecycleActions";
import { NameSourceBadge, TechnicalDetails } from "@/components/runs/PresetIdentity";
import { RunHistoryTable } from "@/components/runs/RunHistoryTable";
import { ModeBadge, RunStateTabs } from "@/components/runs/RunStateTabs";
import { isRunStateSection, runStateIsLive } from "@/lib/runHistory";
import type { CurrencyCode } from "@/lib/currency";
import { debitCurrency, effectiveTradingMode } from "@/lib/tradingMode";
import {
  fetchRun,
  fetchRunAttempts,
  fetchRunBudget,
  fetchRunEvents,
  fetchRunStats,
  keys,
} from "@/lib/queries";
import type {
  MMBudgetSnapshot,
  MMBudgetUsage,
  MMRunStats,
  MMMarketActivity,
  MMMakerActivity,
  MMRunStateSection,
} from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { budgetSourceLabel, runEventLabel, runReasonLabel } from "@/i18n/runtimeCopy";
import { strategyNameLabel } from "@/i18n/strategyCopy";

// The state section is URL-persisted here for the same reason it is on
// preset detail: an operator inspecting Balances who refreshes should
// land back on Balances, and a link they paste should open there.
// Every field is OPTIONAL in the type so an existing <Link to="/runs/$id">
// that passes no search still type-checks — a required search key would
// make every link in the app a compile error. validateSearch still
// normalizes, so the component reads a concrete value.
interface RunSearch {
  section?: MMRunStateSection;
}

export const Route = createFileRoute("/_app/runs/$id")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.runDetail),
  validateSearch: (search: Record<string, unknown>): RunSearch => ({
    section: isRunStateSection(search.section) ? search.section : "orders",
  }),
  component: RunDetailPage,
});

function RunDetailPage() {
  const { t, formatDateTime, formatTime } = useI18n();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const runQ = useQuery({
    queryKey: keys.run(id),
    queryFn: () => fetchRun(id),
    refetchInterval: (q) => (q.state.data?.is_active ? 3_000 : false),
  });
  const run = runQ.data;
  const budgetQ = useQuery({
    queryKey: keys.runBudget(id),
    queryFn: () => fetchRunBudget(id),
    refetchInterval: () => (run?.is_active ? 3_000 : false),
    enabled: !!run,
  });
  const eventsQ = useQuery({
    queryKey: keys.runEvents(id, undefined, 100, 0),
    queryFn: () => fetchRunEvents(id, undefined, 100, 0),
    refetchInterval: () => (run?.is_active ? 5_000 : false),
  });
  const statsQ = useQuery({
    queryKey: keys.runStats(id, 10),
    queryFn: () => fetchRunStats(id, 10),
    refetchInterval: () => (run?.is_active ? 3_000 : false),
    enabled: !!run,
  });
  // The recovery chain. Fetched for every run, not just recovered
  // ones: a run that LATER gets recovered should show the chain
  // without the operator having to guess which attempt to open, and a
  // single-attempt chain renders as one row.
  const attemptsQ = useQuery({
    queryKey: keys.runAttempts(id),
    queryFn: () => fetchRunAttempts(id),
    enabled: !!run,
  });
  const attempts = attemptsQ.data?.runs ?? [];

  const events = eventsQ.data?.events ?? [];
  // Live only while the run is actually EXECUTING — one shared
  // predicate with preset detail, so the two pages cannot disagree
  // about whether a paused run is worth polling.
  const stateIsLive = runStateIsLive(run);

  return (
    <div className="space-y-6">
      <header>
        <div className="text-2xs uppercase tracking-widest t-mute">
          <Link to="/runs" className="hover:t-emph">
            {t("runs.title")}
          </Link>
          {" / "}
          <span className="text-t-1">{run?.preset_name ?? id.slice(0, 14)}</span>
        </div>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {/* The preset NAME leads. The run id moved to the copyable
                technical strip below: it is what actions address, but
                it is not what an operator reads to know which bot this
                is. */}
            <h1 className="page-title flex flex-wrap items-center gap-2">
              <span className="truncate">{run?.preset_name ?? t("common.loading")}</span>
              {run && <ModeBadge mode={run.trading_mode} />}
              {run && <StatusBadge status={run.status} />}
              {run && <NameSourceBadge source={run.preset_name_source} />}
              {run && (run.recovery?.attempt ?? 1) > 1 && (
                <Badge tone="warn" title={run.recovery.resumed_from_run_id}>
                  {t("history.attemptOf", {
                    attempt: run.recovery.attempt,
                    source: run.recovery.resumed_from_run_id?.slice(0, 8) ?? "—",
                  })}
                </Badge>
              )}
            </h1>
            <p className="page-subtitle">
              {run ? (
                <>
                  {t("run.strategy")}{" "}
                  <span className="text-t-1">
                    {strategyNameLabel(run.config.strategy.name, t)}
                  </span>
                </>
              ) : (
                t("common.loading")
              )}
            </p>
            <TechnicalDetails presetID={run?.preset_id || undefined} runID={id} />
            {run?.preset_id && (
              <Link
                to="/presets/$id"
                params={{ id: run.preset_id }}
                className="mt-1 inline-block text-xs t-emph hover:underline"
              >
                {t("run.openPreset")}
              </Link>
            )}
          </div>
          {run && (
            <div className="shrink-0">
              <LifecycleActions run={run} />
            </div>
          )}
        </div>
      </header>
      <DataFreshness query={runQ} relatedQueries={[budgetQ, eventsQ, statsQ, attemptsQ]} />

      <ErrorBanner error={run?.last_error} fallbackTitle={t("run.error.reported")} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">{t("run.started")}</div>
          <div className="mt-2 text-sm text-t-1">
            {run?.start_at ? formatDateTime(run.start_at) : "—"}
          </div>
          <div className="mt-1 text-xs t-mute">
            {t("run.requested", { time: run ? formatDateTime(run.start_requested_at) : "—" })}
          </div>
        </Card>
        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">{t("run.stopped")}</div>
          <div className="mt-2 text-sm text-t-1">
            {run?.stop_at ? formatDateTime(run.stop_at) : "—"}
          </div>
          <div className="mt-1 text-xs t-mute">
            {run?.stop_reason
              ? t("run.stopReason", { reason: runReasonLabel(run.stop_reason, t) })
              : ""}
          </div>
        </Card>
        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">{t("run.lifecycleTimes")}</div>
          <div className="mt-2 text-xs">
            <div className="t-mute">{t("run.createdBy")}</div>
            <div className="text-t-1">{run?.created_by || "—"}</div>
            {run?.paused_at && (
              <>
                <div className="mt-1 t-mute">{t("run.pausedAt")}</div>
                <div className="text-t-1">{formatDateTime(run.paused_at)}</div>
              </>
            )}
            {run?.resumed_at && (
              <>
                <div className="mt-1 t-mute">{t("run.resumedAt")}</div>
                <div className="text-t-1">{formatDateTime(run.resumed_at)}</div>
              </>
            )}
            {run?.stopped_by && (
              <>
                <div className="mt-1 t-mute">{t("run.stoppedBy")}</div>
                <div className="text-t-1">{run.stopped_by}</div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Recovery context. Shown for a FAILED run even when no
          checkpoint exists, because "there is nothing to restore from"
          is exactly what an operator staring at a failure needs told. */}
      {run && (run.status === "FAILED" || (run.recovery?.attempt ?? 1) > 1) && (
        <SectionCard title={t("recovery.title")}>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-2xs uppercase tracking-widest text-t-3">{t("recovery.attempt")}</dt>
              <dd className="mt-1 text-sm text-t-1">{run.recovery?.attempt ?? 1}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-widest text-t-3">{t("recovery.rootRun")}</dt>
              <dd className="mono mt-1 truncate text-sm text-t-1" title={run.recovery?.root_run_id}>
                {run.recovery?.root_run_id ?? run.run_id}
              </dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-widest text-t-3">
                {t("recovery.checkpointStage")}
              </dt>
              <dd className="mt-1 text-sm text-t-1">
                {run.checkpoint?.available ? (run.checkpoint.stage ?? "—") : t("presetDetail.noCheckpoint")}
              </dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-widest text-t-3">
                {t("presetDetail.checkpointFreshness")}
              </dt>
              <dd className="mt-1 text-sm text-t-1">
                {run.checkpoint?.available && run.checkpoint.captured_at
                  ? formatDateTime(run.checkpoint.captured_at)
                  : "—"}
              </dd>
            </div>
          </dl>
        </SectionCard>
      )}

      {/* SM-152 funding block — only present on the detail read in
          system_transfer mode (fallback / ledger-down reads omit it),
          so the card disappears entirely rather than rendering empty. */}
      {run?.funding?.mode === "system" && (
        <SectionCard title={t("run.funding")}>
          <FundingPanel info={run.funding} />
        </SectionCard>
      )}

      {/* The state view. Identical component and vocabulary to
          Preset → Live state, so an operator learns one table. */}
      <SectionCard title={t("run.stateTitle")}>
        <RunStateTabs
          runID={id}
          live={stateIsLive}
          section={search.section ?? "orders"}
          onSectionChange={(section) =>
            navigate({ search: (prev) => ({ ...prev, section }), replace: true })
          }
        />
      </SectionCard>

      <SectionCard
        title={
          <>
            {t("run.budgets")}
            {budgetQ.data && (
              <Badge tone="neutral" className="ml-2">
                {budgetSourceLabel(budgetQ.data.source, t)}
              </Badge>
            )}
          </>
        }
      >
        {budgetQ.isLoading && <Skeleton lines={3} />}
        {budgetQ.data && run && (
          <BudgetGrid
            snapshot={budgetQ.data}
            currency={debitCurrency(run.trading_mode ?? effectiveTradingMode(run.config))}
          />
        )}
      </SectionCard>

      <SectionCard
        title={
          <>
            {t("run.orderDistribution")}
            {statsQ.data && (
              <Badge tone="neutral" className="ml-2">
                {t("run.liveTop", { count: statsQ.data.top_markets?.length ?? 0 })}
              </Badge>
            )}
          </>
        }
      >
        {statsQ.isLoading && !statsQ.data && <Skeleton lines={3} />}
        {statsQ.data === null && <p className="t-mute text-sm">{t("run.statsInactive")}</p>}
        {statsQ.data && <StatsView snapshot={statsQ.data} />}
      </SectionCard>

      {/* The attempt chain. One row for a run that was never
          recovered; the whole lineage for one that was. */}
      {attempts.length > 0 && (
        <SectionCard title={t("recovery.attemptsTitle")}>
          {attemptsQ.isLoading && <Skeleton lines={3} />}
          <RunHistoryTable runs={attempts} />
        </SectionCard>
      )}

      <SectionCard title={t("run.eventLog")}>
        {eventsQ.isLoading && <Skeleton lines={5} />}
        {eventsQ.data && events.length === 0 && (
          <p className="t-mute text-sm">{t("run.noEvents")}</p>
        )}
        {eventsQ.data && events.length > 0 && (
          <div className="space-y-2">
            {events.map((e) => (
              <div
                key={e.event_id}
                className="flex items-start gap-3 rounded-md border border-line-1 bg-bg-3 p-2.5"
              >
                <div className="font-mono text-2xs t-mute pt-0.5 min-w-[88px]">
                  {formatTime(e.occurred_at)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge tone={eventTone(e.kind)}>{runEventLabel(e.kind, t)}</Badge>
                  </div>
                  {Object.keys(e.payload || {}).length > 0 && (
                    <pre className="mt-1.5 overflow-x-auto rounded bg-bg-2 p-2 text-2xs t-mute">
                      {JSON.stringify(e.payload, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title={t("run.snapshot")}>
        {run && (
          <pre className="overflow-x-auto rounded-md border border-line-1 bg-bg-2 p-3 text-2xs t-mute">
            {JSON.stringify(run.config, null, 2)}
          </pre>
        )}
      </SectionCard>
    </div>
  );
}

function BudgetGrid({
  snapshot,
  currency,
}: {
  snapshot: MMBudgetSnapshot;
  // The run's debit currency (CASH → GOLD, GAME → RED): notional is what
  // its orders commit, so it is drawn in that currency's coin.
  currency: CurrencyCode;
}) {
  const { t } = useI18n();
  // Build a (scope → usage) lookup so we can render one row per scope
  // even if the tracker hasn't initialized that scope's counter yet.
  const byScope = new Map<string, MMBudgetUsage>();
  for (const u of snapshot.usage ?? []) byScope.set(u.scope, u);
  const rows: {
    scope: "global" | "maker" | "taker";
    ordersLimit: number;
    notionalLimit: number;
  }[] = [
    {
      scope: "global",
      ordersLimit: snapshot.limits.global_max_orders,
      notionalLimit: snapshot.limits.global_max_notional,
    },
    {
      scope: "maker",
      ordersLimit: snapshot.limits.maker_max_orders,
      notionalLimit: snapshot.limits.maker_max_notional,
    },
    {
      scope: "taker",
      ordersLimit: snapshot.limits.taker_max_orders,
      notionalLimit: snapshot.limits.taker_max_notional,
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {rows.map((r) => {
        const u = byScope.get(r.scope);
        return (
          <div
            key={r.scope}
            className="space-y-2 rounded-md border border-line-1 bg-bg-3 p-3"
          >
            <div className="text-2xs uppercase tracking-widest text-t-3">
              {t(`editor.scope.${r.scope}`)}
            </div>
            <Gauge
              label={t("run.orders")}
              used={u?.orders_placed ?? 0}
              cap={r.ordersLimit}
            />
            <Gauge
              label={t("run.notional")}
              used={u?.notional_minor ?? 0}
              cap={r.notionalLimit}
              currency={currency}
            />
            <div className="text-2xs t-mute">
              {t("run.filled", { count: u?.orders_filled ?? 0 })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Gauge({
  label,
  used,
  cap,
  currency,
}: {
  label: string;
  used: number;
  cap: number;
  // When set, `used`/`cap` are int64 minor-unit notional values — money,
  // not prices — and render as CurrencyAmount with this currency's coin
  // (SM-986; they used to read "$12.00"). Unset = raw counts.
  currency?: CurrencyCode;
}) {
  const { formatNumber } = useI18n();
  const ratio = cap > 0 ? Math.min(1, used / cap) : 0;
  const pct = Math.round(ratio * 100);
  const color =
    cap === 0
      ? "var(--opx-t-3)"
      : ratio >= 1
        ? "var(--opx-no)"
        : ratio >= 0.8
          ? "var(--opx-warn)"
          : "var(--opx-neon)";
  return (
    <div>
      <div className="mb-0.5 flex justify-between text-xs">
        <span className="text-t-2">{label}</span>
        <span className="mono text-t-1">
          {currency ? <CurrencyAmount currency={currency} value={used} /> : formatNumber(used)}{" "}
          <span className="t-mute">/</span>{" "}
          {cap === 0 ? "∞" : currency ? <CurrencyAmount currency={currency} value={cap} /> : formatNumber(cap)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-bg-2">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: cap === 0 ? "100%" : `${pct}%`,
            background: color,
            opacity: cap === 0 ? 0.2 : 1,
          }}
        />
      </div>
    </div>
  );
}

// StatsView renders the senior-quant breakdown of an active MM run:
// per-direction counters (place/cancel × attempt/ok/failed), the
// buy vs sell split, the maker vs taker pool split, the error-code
// histogram, and the top-N busiest markets and makers. All values
// come from the in-memory tracker; nothing is computed client-side
// beyond percentages and formatting.
function StatsView({ snapshot }: { snapshot: MMRunStats }) {
  const { t, formatNumber, formatTime } = useI18n();
  const totalSide = snapshot.buy_orders + snapshot.sell_orders;
  const buyPct =
    totalSide > 0 ? Math.round((snapshot.buy_orders / totalSide) * 100) : 0;
  const totalPool = snapshot.maker_orders + snapshot.taker_orders;
  const makerPct =
    totalPool > 0 ? Math.round((snapshot.maker_orders / totalPool) * 100) : 0;
  const errs = snapshot.errors_by_code ?? {};
  const errEntries = Object.entries(errs).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      {/* Top-level counter grid — place row + cancel row, each split
          into attempts / ok / failed so a quant can spot a dominant
          failure mode at a glance. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <CounterTile label={t("run.placeAttempts")} value={snapshot.place_attempts} />
        <CounterTile label={t("run.placeOk")} value={snapshot.place_ok} tone="yes" />
        <CounterTile
          label={t("run.placeFailed")}
          value={snapshot.place_failed}
          tone="no"
        />
        <CounterTile label={t("run.cancelAttempts")} value={snapshot.cancel_attempts} />
        <CounterTile label={t("run.cancelOk")} value={snapshot.cancel_ok} tone="yes" />
        <CounterTile
          label={t("run.cancelFailed")}
          value={snapshot.cancel_failed}
          tone="no"
        />
      </div>

      {/* Side + pool split bars. 50/50 is the healthy steady state for
          a symmetric maker fleet; runaway one-sidedness flags adverse
          selection or a stuck maker side. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <DistroBar
          title={t("run.buyVsSell")}
          leftLabel={t("run.buyCount", { count: formatNumber(snapshot.buy_orders) })}
          rightLabel={t("run.sellCount", { count: formatNumber(snapshot.sell_orders) })}
          leftPct={buyPct}
          tone="yesNo"
        />
        <DistroBar
          title={t("run.makerVsTaker")}
          leftLabel={t("run.makerCount", { count: formatNumber(snapshot.maker_orders) })}
          rightLabel={t("run.takerCount", { count: formatNumber(snapshot.taker_orders) })}
          leftPct={makerPct}
          tone="accent"
        />
      </div>

      {/* Error-code histogram. Empty means "all ok"; a single code
          dominating (e.g. MM_000300 MMBudgetExhausted) tells the
          operator exactly what to look at. */}
      {errEntries.length > 0 && (
        <div className="rounded-md border border-line-1 bg-bg-3 p-3">
          <div className="mb-2 text-2xs uppercase tracking-widest text-t-3">
            {t("run.errorsByCode")}
          </div>
          <div className="flex flex-wrap gap-2">
            {errEntries.map(([code, n]) => (
              <span key={code} className="rounded bg-bg-2 px-2 py-1 text-xs">
                <span className="mono text-t-1">{code}</span>{" "}
                <span className="t-mute">×</span>{" "}
                <span className="mono">{formatNumber(n)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Top-N markets + makers side-by-side. Sorted by total
          attempts on the server; the empty state means the tracker
          hasn't seen any traffic yet (run just started or stuck). */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TopMarketsTable rows={snapshot.top_markets ?? []} />
        <TopMakersTable rows={snapshot.top_makers ?? []} />
      </div>

      <p className="text-2xs t-mute">
        {t("run.updatedSince", {
          updated: formatTime(snapshot.updated_at),
          since: formatTime(snapshot.started_at),
        })}
      </p>
    </div>
  );
}

function CounterTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "yes" | "no";
}) {
  const { formatNumber } = useI18n();
  const color =
    tone === "yes"
      ? "var(--opx-yes)"
      : tone === "no"
        ? "var(--opx-no)"
        : "var(--opx-t-1)";
  return (
    <div className="rounded-md border border-line-1 bg-bg-3 p-3">
      <div className="text-2xs uppercase tracking-widest text-t-3">{label}</div>
      <div className="mt-1 font-mono text-lg" style={{ color }}>
        {formatNumber(value)}
      </div>
    </div>
  );
}

function DistroBar({
  title,
  leftLabel,
  rightLabel,
  leftPct,
  tone,
}: {
  title: string;
  leftLabel: string;
  rightLabel: string;
  leftPct: number;
  tone: "yesNo" | "accent";
}) {
  const leftBg = tone === "yesNo" ? "var(--opx-yes)" : "var(--opx-neon)";
  const rightBg = tone === "yesNo" ? "var(--opx-no)" : "var(--opx-warn)";
  return (
    <div className="rounded-md border border-line-1 bg-bg-3 p-3">
      <div className="mb-2 text-2xs uppercase tracking-widest text-t-3">
        {title}
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="mono text-t-1 min-w-fit">{leftLabel}</span>
        <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-bg-2">
          <div style={{ width: `${leftPct}%`, background: leftBg }} />
          <div style={{ width: `${100 - leftPct}%`, background: rightBg }} />
        </div>
        <span className="mono text-t-1 min-w-fit">{rightLabel}</span>
      </div>
    </div>
  );
}

function TopMarketsTable({ rows }: { rows: MMMarketActivity[] }) {
  const { t, formatNumber } = useI18n();
  return (
    <div className="rounded-md border border-line-1 bg-bg-3 p-3">
      <div className="mb-2 text-2xs uppercase tracking-widest text-t-3">
        {t("run.topMarkets")}
      </div>
      {rows.length === 0 ? (
        <p className="t-mute text-xs">{t("run.noActivity")}</p>
      ) : (
        <table className="tbl rounded-none border-0">
          <thead>
            <tr>
              <th>{t("common.market")}</th>
              <th className="text-right">{t("run.placed")}</th>
              <th className="text-right">{t("run.placeOkShort")}</th>
              <th className="text-right">{t("run.placeFailedShort")}</th>
              <th className="text-right">{t("run.cancelOkShort")}</th>
              <th className="text-right">{t("run.cancelFailedShort")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.market_id}>
                <td className="mono text-xs">{r.market_id}</td>
                <td className="amt text-right">
                  {formatNumber(r.place_attempts)}
                </td>
                <td className="amt text-right text-yes">
                  {formatNumber(r.place_ok)}
                </td>
                <td className="amt text-right text-no">
                  {formatNumber(r.place_failed)}
                </td>
                <td className="amt text-right text-yes">
                  {formatNumber(r.cancel_ok)}
                </td>
                <td className="amt text-right text-no">
                  {formatNumber(r.cancel_failed)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TopMakersTable({ rows }: { rows: MMMakerActivity[] }) {
  const { t, formatNumber } = useI18n();
  return (
    <div className="rounded-md border border-line-1 bg-bg-3 p-3">
      <div className="mb-2 text-2xs uppercase tracking-widest text-t-3">
        {t("run.topMakers")}
      </div>
      {rows.length === 0 ? (
        <p className="t-mute text-xs">{t("run.noActivity")}</p>
      ) : (
        <table className="tbl rounded-none border-0">
          <thead>
            <tr>
              <th>{t("common.user")}</th>
              <th className="text-right">{t("run.placed")}</th>
              <th className="text-right">{t("run.placeOkShort")}</th>
              <th className="text-right">{t("run.placeFailedShort")}</th>
              <th className="text-right">{t("run.cancelOkShort")}</th>
              <th className="text-right">{t("run.cancelFailedShort")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id}>
                <td className="mono text-xs">{r.user_id.slice(0, 18)}…</td>
                <td className="amt text-right">
                  {formatNumber(r.place_attempts)}
                </td>
                <td className="amt text-right text-yes">
                  {formatNumber(r.place_ok)}
                </td>
                <td className="amt text-right text-no">
                  {formatNumber(r.place_failed)}
                </td>
                <td className="amt text-right text-yes">
                  {formatNumber(r.cancel_ok)}
                </td>
                <td className="amt text-right text-no">
                  {formatNumber(r.cancel_failed)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function eventTone(kind: string): "neutral" | "accent" | "yes" | "no" | "warn" {
  switch (kind) {
    case "STARTED":
      return "yes";
    case "STOPPED":
      return "neutral";
    case "FAILED":
    case "PLACE_FAIL":
    case "CANCEL_FAIL":
      return "no";
    case "BUDGET_PAUSED":
    case "STOP_REQUESTED":
    case "FUNDING_INSUFFICIENT":
      return "warn";
    case "PLACE_OK":
    case "CANCEL_OK":
    case "FILL":
    case "INSTRUMENT_ADDED":
      return "accent";
    default:
      return "neutral";
  }
}
